import { db } from './firebase-config.js';
import { doc, getDoc, updateDoc, setDoc, increment, runTransaction } from 'firebase/firestore';

// Debug helper
const DEBUG = true;
function debug(message, data = null) {
  if (DEBUG) {
    if (data) {
      console.log(`[Debug] ${message}`, data);
    } else {
      console.log(`[Debug] ${message}`);
    }
  }
}

// Theme state management
const themeState = {
  current: null,
  transitioning: false
};

// Track initialization state
let isInitialized = false;
let viewCountUpdating = false;

// Initialize theme on page load
function initTheme() {
  try {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const theme = savedTheme || (prefersDark ? 'dark' : 'light');
    
    applyTheme(theme);
    themeState.current = theme;
    
    // Watch for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem('theme')) {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    });
  } catch (error) {
    console.error('Theme initialization error:', error);
  }
}

// Apply theme to document
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.body.setAttribute('data-theme', theme);
  localStorage.setItem('theme', theme);
  themeState.current = theme;
}

// Toggle theme with transition handling
function toggleTheme(e) {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  if (themeState.transitioning) return;
  
  themeState.transitioning = true;
  const next = themeState.current === 'light' ? 'dark' : 'light';
  
  // Add transition class
  document.documentElement.classList.add('theme-transition');
  
  // Apply new theme
  applyTheme(next);
  
  // Remove transition class after animation
  setTimeout(() => {
    document.documentElement.classList.remove('theme-transition');
    themeState.transitioning = false;
  }, 300);
}

// Menu state management
const menuState = {
  isOpen: false,
  isTransitioning: false
};

// Event delegation handler
function handleGlobalClick(e) {
  debug('Global click on:', e.target.className);
  
  // Theme toggle handling
  if (e.target.closest('.theme-toggle')) {
    debug('Theme toggle clicked');
    toggleTheme(e);
    return;
  }
  
  // Menu icon handling
  if (e.target.closest('.icon-menu')) {
    debug('Menu icon clicked');
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
    return;
  }
  
  // Menu link handling
  if (e.target.closest('.menu__link')) {
    debug('Menu link clicked');
    if (menuState.isOpen) {
      closeMenu(true);
    }
    return;
  }
  
  // Like button handling
  if (e.target.closest('.like-button')) {
    debug('Like button clicked (global handler)');
    e.preventDefault();
    e.stopPropagation();
    return;  // Let the specific handler deal with it
  }
  
  // Outside click handling
  if (menuState.isOpen && 
      !e.target.closest('.menu__body') && 
      !e.target.closest('.icon-menu')) {
    debug('Outside menu clicked');
    closeMenu();
  }
}

function toggleMenu() {
  if (menuState.isTransitioning) return;
  if (menuState.isOpen) {
    closeMenu();
  } else {
    openMenu();
  }
}

function closeMenu(immediate = false) {
  if (menuState.isTransitioning && !immediate) return;
  
  menuState.isTransitioning = true;
  menuState.isOpen = false;
  
  document.body.classList.remove('menu-open');
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.height = '';
  
  const menuBody = document.querySelector('.menu__body');
  if (menuBody) {
    const handleTransitionEnd = () => {
      menuState.isTransitioning = false;
      menuBody.removeEventListener('transitionend', handleTransitionEnd);
    };
    menuBody.addEventListener('transitionend', handleTransitionEnd);
  }
}

function openMenu() {
  if (menuState.isTransitioning) return;
  
  menuState.isTransitioning = true;
  menuState.isOpen = true;
  
  document.body.classList.add('menu-open');
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.height = '100%';
  
  const menuBody = document.querySelector('.menu__body');
  if (menuBody) {
    const handleTransitionEnd = () => {
      menuState.isTransitioning = false;
      menuBody.removeEventListener('transitionend', handleTransitionEnd);
    };
    menuBody.addEventListener('transitionend', handleTransitionEnd);
  }
}

// Initialize likes
async function initializeLikes() {
  debug('Initializing likes');
  try {
    const likeButtons = document.querySelectorAll('.like-button');
    debug(`Found ${likeButtons.length} like buttons`);

    for (const button of likeButtons) {
      const postId = button.dataset.postId;
      if (!postId) {
        console.error('Like button missing post-id:', button);
        continue;
      }

      // Get current likes
      const likesRef = doc(db, 'likes', postId);
      const likesDoc = await getDoc(likesRef);
      
      // Update UI
      const count = likesDoc.exists() ? likesDoc.data().count : 0;
      const countElement = button.querySelector('.like-count');
      if (countElement) {
        countElement.textContent = count;
        debug(`Updated like count for ${postId}: ${count}`);
      }

      // Check if liked
      const liked = localStorage.getItem(`liked_${postId}`);
      if (liked === 'true') {
        button.classList.add('liked');
        debug(`Post ${postId} was previously liked`);
      }

      // Add click handler
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleLike(postId, button);
      }, { once: true });  // Ensure handler is only added once
    }
  } catch (error) {
    console.error('Failed to initialize likes:', error);
  }
}

// Handle like click
async function handleLike(postId, button) {
  // Prevent multiple rapid clicks
  if (button.dataset.processing === 'true') {
    debug('Like button is already processing');
    return;
  }
  button.dataset.processing = 'true';

  debug(`Like clicked for post: ${postId}`);
  try {
    const likesRef = doc(db, 'likes', postId);
    const countElement = button.querySelector('.like-count');
    const isLiked = localStorage.getItem(`liked_${postId}`) === 'true';

    if (isLiked) {
      // Unlike
      await updateDoc(likesRef, {
        count: increment(-1)
      });
      localStorage.removeItem(`liked_${postId}`);
      button.classList.remove('liked');
      if (countElement) {
        const newCount = parseInt(countElement.textContent) - 1;
        countElement.textContent = newCount;
        debug(`Unliked post ${postId}, new count: ${newCount}`);
      }
    } else {
      // Like
      const likesDoc = await getDoc(likesRef);
      if (!likesDoc.exists()) {
        await setDoc(likesRef, { count: 1 });
      } else {
        await updateDoc(likesRef, {
          count: increment(1)
        });
      }
      localStorage.setItem(`liked_${postId}`, 'true');
      button.classList.add('liked');
      if (countElement) {
        const newCount = parseInt(countElement.textContent) + 1;
        countElement.textContent = newCount;
        debug(`Liked post ${postId}, new count: ${newCount}`);
      }
    }
  } catch (error) {
    console.error('Failed to update like:', error);
    alert('Failed to update like. Please try again.');
  } finally {
    // Re-enable the button
    button.dataset.processing = 'false';
    
    // Re-add click handler for future clicks
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleLike(postId, button);
    }, { once: true });
  }
}

// View counting
async function incrementViewCount() {
  if (viewCountUpdating) {
    debug('🔒 View count update already in progress, skipping');
    return;
  }
  viewCountUpdating = true;

  try {
    debug('🔍 === Starting View Count Update ===');
    debug('Current URL:', window.location.href);
    debug('Current pathname:', window.location.pathname);
    
    // Get post ID from either URL or data attribute
    let postId = window.location.pathname.split('/posts/')[1]?.replace(/\/$/, '');
    debug('📄 Extracted postId:', { 
      raw: window.location.pathname.split('/posts/')[1],
      cleaned: postId,
      fullPath: window.location.pathname 
    });
    
    if (!postId) {
      debug('❌ No postId found. URL structure might be incorrect');
      debug('Expected format: /posts/[postId]/');
      return;
    }

    const viewsRef = doc(db, 'views', postId);
    debug('🔥 Firebase Reference:', {
      collection: 'views',
      docId: postId,
      path: viewsRef.path
    });
    
    // Use transaction to ensure accurate count
    debug('🔄 Starting Firebase transaction');
    await runTransaction(db, async (transaction) => {
      const viewsDoc = await transaction.get(viewsRef);
      debug('📊 Current document state:', {
        exists: viewsDoc.exists(),
        data: viewsDoc.exists() ? viewsDoc.data() : null,
        path: viewsDoc.ref.path
      });
      
      if (!viewsDoc.exists()) {
        debug('🆕 Creating new view document:', {
          postId,
          initialCount: 1
        });
        transaction.set(viewsRef, { count: 1 });
        return 1;
      } else {
        const newCount = viewsDoc.data().count + 1;
        debug('⬆️ Updating existing view count:', {
          postId,
          oldCount: viewsDoc.data().count,
          newCount,
          change: '+1'
        });
        transaction.update(viewsRef, { count: newCount });
        return newCount;
      }
    });
    debug('✅ Firebase transaction completed');

    // Get updated count to verify
    debug('🔍 Verifying updated count');
    const updatedDoc = await getDoc(viewsRef);
    debug('📊 Database state after update:', {
      exists: updatedDoc.exists(),
      data: updatedDoc.data(),
      path: updatedDoc.ref.path
    });
    const viewCount = updatedDoc.exists() ? updatedDoc.data().count : 0;
    
    // Update UI with animation
    const viewCountElements = document.querySelectorAll(`[data-post-id="${postId}"] .view-count`);
    debug('🎯 Found view count elements:', {
      count: viewCountElements.length,
      elements: Array.from(viewCountElements).map(el => ({
        postId: el.closest('[data-post-id]')?.dataset.postId,
        currentText: el.textContent
      })),
      selectors: {
        used: `[data-post-id="${postId}"] .view-count`,
        allViewCounts: document.querySelectorAll('.view-count').length,
        allPostIds: document.querySelectorAll('[data-post-id]').length
      },
      documentBody: document.body.innerHTML.includes(postId) ? 'Contains postId' : 'Missing postId'
    });
    
    if (viewCountElements.length === 0) {
      debug('⚠️ No view count elements found. Checking post template...');
    }
    
    viewCountElements.forEach(viewCountElement => {
      debug('🔄 Updating element:', {
        before: viewCountElement.textContent,
        after: viewCount,
        postId: viewCountElement.closest('[data-post-id]')?.dataset.postId
      });
      viewCountElement.classList.add('updating');
      viewCountElement.textContent = `${viewCount}`;
      setTimeout(() => {
        viewCountElement.classList.remove('updating');
        debug('✨ Animation complete for element');
      }, 500);
    });
    debug('=== View Count Update Complete ===');
  } catch (error) {
    console.error('❌ Failed to update view count:', error);
    debug('Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
  } finally {
    viewCountUpdating = false;
  }
}

// Initialize view counts for post cards
async function initializeViewCounts() {
  try {
    debug('=== Initializing View Counts ===');
    const viewElements = document.querySelectorAll('.post-card__views');
    debug('Found view elements:', viewElements.length);

    for (const element of viewElements) {
      const postId = element.dataset.postId;
      debug('Processing element:', { postId });
      if (!postId) {
        debug('⚠️ Skipping element - no postId');
        continue;
      }

      const viewsRef = doc(db, 'views', postId);
      const viewsDoc = await getDoc(viewsRef);
      const count = viewsDoc.exists() ? viewsDoc.data().count : 0;
      debug('📊 Retrieved count for', postId, ':', count);

      const countElement = element.querySelector('.view-count');
      if (countElement) {
        countElement.textContent = count;
        debug('✅ Updated count display for', postId);
      } else {
        debug('⚠️ No count element found for', postId);
      }
    }
    debug('=== View Count Initialization Complete ===');
  } catch (error) {
    console.error('❌ Failed to initialize view counts:', error);
    debug('Error details:', {
      message: error.message,
      code: error.code,
      stack: error.stack
    });
  }
}

// Initialize everything
async function init() {
  // Prevent double initialization
  if (isInitialized) {
    debug('🚫 Already initialized, skipping');
    return;
  }
  isInitialized = true;

  debug('Initializing...');
  
  // Initialize theme first
  initTheme();
  
  // Initialize likes
  await initializeLikes();
  
  // Initialize view counts for listing pages
  await initializeViewCounts();
  
  // Only increment view count if we're on a post page
  if (window.location.pathname.includes('/posts/')) {
    debug('📑 On a post page, incrementing view count');
    await incrementViewCount();
  } else {
    debug('📑 Not on a post page, skipping view count increment');
  }
  
  // Setup global click handler
  document.addEventListener('click', handleGlobalClick);
  
  // Handle escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menuState.isOpen) {
      closeMenu();
    }
  });
  
  debug('Initialization complete');
}

// Initialize only once when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Handle page transitions
window.addEventListener('pageshow', (event) => {
  // Re-initialize theme without animation
  initTheme();
  
  // Reset menu if needed
  if (event.persisted) {
    closeMenu(true);
  }
});

// Handle orientation changes
window.addEventListener('orientationchange', () => {
  closeMenu(true);
});
